import type { ReactNode } from 'react';

import { BLOCKS } from '@contentful/rich-text-types';

import {
  BpkAccordionItem,
  withAccordionItemState,
} from '@skyscanner/backpack-web/bpk-component-accordion';
import { TEXT_STYLES } from '@skyscanner/backpack-web/bpk-component-text';
import BpkText from '@skyscanner/backpack-web/bpk-component-text/src/BpkText';

import RichText from '../../RichText';
import RichTextAnchor from '../../RichTextAnchor';
import RichTextButton from '../../RichTextButton';
import RichTextTables from '../../RichTextTables';
import Icon from '../../common/Icon';
import { useMBDContext } from '../../common/MBD/useMBDContext';

import type { Node } from '@contentful/rich-text-types';
import type { AccordionItemProps } from '@skyscanner-internal/landing-pages-shared-types/src/types-shared/AccordionProps';

import STYLES from './AccordionItem.module.scss';

const StatefulAccordionItem = withAccordionItemState(BpkAccordionItem);

const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: Node, children: ReactNode) => (
      <BpkText tagName="p" textStyle={TEXT_STYLES.bodyDefault}>
        {children}
      </BpkText>
    ),
  },
};

const AccordionItem = ({
  icon,
  id,
  isFaq,
  richText,
  richTextAnchor,
  richTextButton,
  richTextTables,
  title,
}: AccordionItemProps) => {
  let IconComponent;

  if (icon) {
    IconComponent = Icon(icon);
  }

  const mbdProps: { [key: string]: string } = { accordion_id: id };
  const { buildMBDParam, rawComponentName } = useMBDContext();

  return (
    <div
      className={STYLES.AccordionItem}
      data-tracking-element-id={`${rawComponentName}_item_${id}`}
      data-tracking-common-params={buildMBDParam(
        `${rawComponentName}_item`,
        mbdProps,
      )}
    >
      <StatefulAccordionItem
        key={id}
        id={id}
        title={title}
        tagName="h3"
        icon={IconComponent && <IconComponent />}
      >
        {richText && <RichText {...richText} options={richTextOptions} />}
        {richTextButton && (
          <RichTextButton
            {...richTextButton}
            richTextProps={{
              ...richTextButton.richTextProps,
              options: richTextOptions,
            }}
          />
        )}
        {richTextAnchor && (
          <RichTextAnchor
            {...richTextAnchor}
            richTextProps={{
              ...richTextAnchor.richTextProps,
              options: richTextOptions,
            }}
          />
        )}
        {richTextTables && (
          <RichTextTables
            {...richTextTables}
            richTextProps={{
              ...richTextTables.richTextProps,
              options: richTextOptions,
            }}
          />
        )}
      </StatefulAccordionItem>
    </div>
  );
};

export default AccordionItem;
